import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  MAX_VIDEO_SIZE_BYTES,
  MAX_VISIBLE_VIDEOS,
  useTrendingVideo,
} from '../../../../apps/hooks/video/useTrendingVideo';
import { ROUTES } from '../../../../routes/routes';

const MB = 1024 * 1024;
const MAX_VIDEO_SIZE_MB = MAX_VIDEO_SIZE_BYTES / MB;

export default function TrendingVideoUploadPage() {
  const navigate = useNavigate();
  const trendingVideo = useTrendingVideo();

  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [productFocus, setProductFocus] = useState('');
  const [productLink, setProductLink] = useState('');
  const [contentLink, setContentLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onDrop = useCallback(
    async (accepted: File[]) => {
      setError(null);
      setSuccess(null);
      const file = accepted[0];
      if (!file) {
        return;
      }

      setStagedFile(file);
      setVideoUrl(null);

      const uploadResult = await trendingVideo.uploadFile(file);
      if (uploadResult) {
        setVideoUrl(uploadResult.videoUrl);
      }
    },
    [trendingVideo],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/mp4': ['.mp4'] },
    multiple: false,
    maxSize: MAX_VIDEO_SIZE_BYTES,
  });

  const retryUpload = useCallback(async () => {
    if (!stagedFile) {
      return;
    }
    setError(null);
    const uploadResult = await trendingVideo.uploadFile(stagedFile);
    if (uploadResult) {
      setVideoUrl(uploadResult.videoUrl);
    }
  }, [stagedFile, trendingVideo]);

  const clearStaged = () => {
    if (stagedFile) {
      trendingVideo.clearUploadingFile(stagedFile.name);
    }
    setStagedFile(null);
    setVideoUrl(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!videoUrl) {
      setError('Please upload a video before saving.');
      return;
    }
    if (!productFocus.trim()) {
      setError('Product focus label is required.');
      return;
    }
    if (!productLink.trim()) {
      setError('Product link is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Check visible count to auto-set visibility
      const existing = await trendingVideo.list({ limit: 100 });
      const currentVisibleCount = existing.videos.filter((v) => v.isVisible).length;
      const willBeVisible = currentVisibleCount < MAX_VISIBLE_VIDEOS;

      const payload: Parameters<typeof trendingVideo.create>[0] = {
        videoUrl,
        productFocus: productFocus.trim(),
        productLink: productLink.trim(),
        isVisible: willBeVisible,
      };
      const trimmedContentLink = contentLink.trim();
      if (trimmedContentLink) {
        payload.contentLink = trimmedContentLink;
      }
      const created = await trendingVideo.create(payload);
      const visibilityNote = willBeVisible ? '' : ' (created as hidden — 8 visible videos already reached)';
      setSuccess(`Trending video created (id ${created.videoId})${visibilityNote}. Redirecting to list…`);
      setTimeout(() => navigate(ROUTES.trendingVideo.list), 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save trending video');
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadState = stagedFile ? trendingVideo.uploadingFiles[stagedFile.name] : undefined;
  const uploadInProgress = uploadState !== undefined && uploadState.error === undefined && uploadState.progress < 100;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Upload Trending Video</h2>
        <Button variant="primary" as="a" href={ROUTES.trendingVideo.list}>
          Go to Trending Video List
        </Button>
      </div>

      <div className="text-muted mb-4">
        Upload a short mp4 that will appear in the homepage Trending Now carousel. Videos must be mp4 and not exceed{' '}
        {MAX_VIDEO_SIZE_MB}MB.
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4">
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">1. Upload video file</h5>

            {!stagedFile && (
              <div
                {...getRootProps()}
                className={`border border-2 border-dashed rounded p-5 text-center ${isDragActive ? 'bg-light border-primary' : 'bg-secondary bg-opacity-10 border-secondary'}`}
                style={{ cursor: 'pointer' }}
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p className="mb-0 text-primary fw-bold">Drop the mp4 file here…</p>
                ) : (
                  <p className="mb-0 text-muted">Drag and drop an mp4 file here, or click to select one</p>
                )}
              </div>
            )}

            {stagedFile && (
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong>{stagedFile.name}</strong>
                  <span className="text-muted">{(stagedFile.size / MB).toFixed(2)}MB</span>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Remove file"
                    onClick={clearStaged}
                    style={{ fontSize: '0.65rem' }}
                  />
                  {uploadState?.error && (
                    <Button size="sm" variant="outline-primary" onClick={retryUpload} className="ms-auto">
                      Retry
                    </Button>
                  )}
                </div>
                {uploadState && (
                  <>
                    <div className="progress mb-2" style={{ height: '5px' }}>
                      <div
                        className={`progress-bar ${uploadState.error ? 'bg-danger' : ''}`}
                        style={{ width: `${uploadState.progress}%` }}
                      />
                    </div>
                    {uploadState.error && <small className="text-danger">{uploadState.error}</small>}
                  </>
                )}
                {videoUrl && (
                  <div className="d-flex justify-content-center mt-3">
                    <video
                      src={videoUrl}
                      controls
                      muted
                      style={{ maxWidth: '400px', maxHeight: '500px', borderRadius: '8px' }}
                    />
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">2. Configure</h5>
            <Form.Group className="mb-3" controlId="productFocus">
              <Form.Label>Product focus label</Form.Label>
              <Form.Control
                type="text"
                value={productFocus}
                onChange={(e) => setProductFocus(e.target.value)}
                placeholder="e.g. Dr AG Repair Toner Pad"
                required
              />
              <Form.Text className="text-muted">Shown as an overlay on the video.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="productLink">
              <Form.Label>Product link (PDP URL)</Form.Label>
              <Form.Control
                type="url"
                value={productLink}
                onChange={(e) => setProductLink(e.target.value)}
                placeholder="https://skin-seoul.com/product/{slug}"
                required
              />
              <Form.Text className="text-muted">
                Must be a published product page on skin-seoul.com. The server will reject unpublished or missing
                products.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="contentLink">
              <Form.Label>Source link (optional)</Form.Label>
              <Form.Control
                type="url"
                value={contentLink}
                onChange={(e) => setContentLink(e.target.value)}
                placeholder="e.g. https://www.tiktok.com/@skinseoul.official/video/…"
              />
              <Form.Text className="text-muted">Informational only, e.g. the original TikTok source.</Form.Text>
            </Form.Group>
          </Card.Body>
        </Card>

        <div className="d-flex gap-2 justify-content-end">
          <Button variant="secondary" onClick={() => navigate(ROUTES.trendingVideo.list)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting || uploadInProgress || !videoUrl}>
            {isSubmitting ? 'Saving…' : 'Save trending video'}
          </Button>
        </div>
      </Form>
    </div>
  );
}
